import React, { useState, useEffect } from "react";
import vehicleTools from "../../../../../helpers/vehicleTools";
import useTVA from "../../../../../hooks/useInit/useTVA";
import FormGenerator from "../../../Doc/FormGenerator";
import clientSchema from "./client.schema.json";
import contractSchema from "./contract.schema.json";
import { contractValidation } from "./contractValidation";
import vehicleSchema from "./vehicle.schema.json";
import vehicleUniqueSchema from "./vehicle_unique.schema.json";
import { toast } from "react-toastify";
import useStore from "../../../../../context/useStore";
import { getSchemaWithConstants } from "./getSchemaWithConstants";
import ClientSearchHitech from "./ClientSearchHitech";
import ContractSearchHitech from "./ContractSearchHitech";
import tools from "../../../../../helpers/tools";
import axios from "axios";
import { api_url, hitech_client_url } from "../../../../../config/config";

const entitiesEnum = {
  VEHICLE: "vehicle",
  VEHICLE_UNIQUES: "vehicleUniques",
  CLIENT: "client",
  CONTRACT: "contract",
};

const getInitialValue = (property, fieldName, initialValues) => {
  // Si pas de connexions, utiliser la valeur initiale ou la valeur par défaut
  if (!property.connections) {
    const value = initialValues[fieldName] || property.defaultValue || "";
    return convertToType(value, property.type);
  }
  // Récupérer toutes les valeurs valides des connexions
  const values = property.connections
    .map((connection) => {
      const { schema: schemaName, property: propName } = connection;
      const lowerSchemaName =
        schemaName.charAt(0).toLowerCase() + schemaName.slice(1);

      if (schemaName === "Root") {
        return initialValues[propName];
      } else {
        return initialValues[lowerSchemaName]?.[propName];
      }
    })
    .filter((value) => value !== undefined);

  // Si aucune valeur n'est trouvée, utiliser la valeur par défaut ou une chaîne vide
  if (values.length === 0) {
    const defaultValue = property.defaultValue || "";
    return convertToType(defaultValue, property.type);
  }

  // Si le type est une chaîne, concaténer avec un espace
  if (property.type === "string") {
    return values.join(" ");
  }

  // Pour les types numériques, retourner la première valeur convertie
  return convertToType(values[0], property.type);
};

const convertToType = (value, type) => {
  if (value === null || value === undefined || value === "") {
    return type === "string" ? "" : null;
  }

  switch (type) {
    case "integer":
      return parseInt(value, 10);
    case "number":
      return parseFloat(value);
    case "boolean":
      return Boolean(value);
    default:
      return String(value);
  }
};

const generateState = (schema, initialValues) => {
  const mappedData = {};
  const [schemaName] = Object.keys(schema);
  const properties = schema[schemaName].properties;
  Object.entries(properties).forEach(([fieldName, property]) => {
    mappedData[fieldName] = getInitialValue(property, fieldName, initialValues);
  });

  return mappedData;
};

const parseVehicleData = (data) => {
  const { vehicle } = data;

  return {
    ...vehicle,
    fuelType: vehicle.brandModelFinish?.fuelType?.id,
    assureur: vehicle.insuranceHT > 10 ? "Leasecar" : "Client",
    repurchaseValueFinal: data.repurchaseValueFinal,
    km: data.km,
  };
};

const parseClientData = (data) => {
  const { client } = data;
  const paysEnum = {
    FRANCE: "France",
    REUNION: "La Réunion",
    MAYOTTE: "Mayotte",
  };
  return {
    ...client,
    pays: paysEnum[client.pays] || "",
  };
};

const parseContractData = (data) => {
  return {
    ...data,
  };
};

const ContractGenerator = ({ quotation, onGEnerateContract }) => {
  const [state] = useStore();

  const { getTVAByCatalog } = useTVA();
  const [validationErrors, setValidationErrors] = useState([]);
  const [hitechClientVerification, setHitechClientVerification] = useState({
    loading: false,
    data: null,
    error: null,
  });

  const { TVA, TVA_INVERT } = getTVAByCatalog(quotation.vehicle.catalog.uuid);
  let displayPriceOptions = quotation.vehicle.optionsNotIncluded?.reduce(
    (a, b) => a + b.priceHT * (b.isInclTVA ? TVA_INVERT : 1),
    0,
  );
  var { priceTotalHT, priceTotalTTC } = vehicleTools.calcVehiclePrices(
    quotation.vehicle,
    TVA,
    TVA_INVERT,
  );

  const [activeTab, setActiveTab] = useState("contrat");
  // Create a combined array for SECTEURS from reunion and mayotte arrays

  let combinedSecteurs;
  if (
    quotation?.vehicle?.catalog?.geographicZone?.name === "La Réunion" ||
    quotation?.vehicle?.catalog?.geographic_zone === 1
  ) {
    combinedSecteurs = [
      ...(state.constant.items.CONTRAT.SECTEURS.reunion || []).map((item) => ({
        id: item.id,
        name: `Réunion - ${item.name}`,
      })),
    ];
  } else if (
    quotation?.vehicle?.catalog?.geographicZone?.name === "Mayotte" ||
    quotation?.vehicle?.catalog?.geographic_zone === 2
  ) {
    combinedSecteurs = [
      ...(state.constant.items.CONTRAT.SECTEURS.mayotte || []).map((item) => ({
        id: item.id,
        name: `Mayotte - ${item.name}`,
      })),
    ];
  } else {
    //combine both arrays
    combinedSecteurs = [
      ...(state.constant.items.CONTRAT.SECTEURS.reunion || []).map((item) => ({
        id: item.id,
        name: `Réunion - ${item.name}`,
      })),
      ...(state.constant.items.CONTRAT.SECTEURS.mayotte || []).map((item) => ({
        id: item.id,
        name: `Mayotte - ${item.name}`,
      })),
    ];
  }

  console.log("quotation", quotation);

  const clientSchemaWithConstants = getSchemaWithConstants(clientSchema, {
    ...state.constant.items,
    CONTRAT: {
      ...state.constant.items.CONTRAT,
      SECTEURS: combinedSecteurs,
    },
  });
  const contractSchemaWithConstants = getSchemaWithConstants(
    contractSchema,
    state.constant.items,
  );
  const vehicleSchemaWithConstants = getSchemaWithConstants(
    vehicleSchema,
    state.constant.items,
  );
  const getInitialState = () => {
    return {
      contract: generateState(contractSchemaWithConstants, {
        ...parseContractData(quotation),
      }),
      client: generateState(clientSchemaWithConstants, {
        ...parseClientData(quotation),
      }),
      vehicle: generateState(vehicleSchemaWithConstants, {
        ...parseVehicleData({
          ...quotation,
          vehicle: {
            ...quotation.vehicle,
            vehicleCategory: quotation.vehicleCategory,
            insuranceHT: quotation.insuranceHT,
            prixOptionsTTC: Math.round(displayPriceOptions * 100) / 100,
            tvaAchat: Math.round((priceTotalTTC - priceTotalHT) * 100) / 100,
            priceNetTTC: Math.round(quotation.vehicle.priceNetTTC * 100) / 100,
          },
        }),
      }),
      vehicleUniques: [],
    };
  };

  const [contractData, setContractData] = useState(getInitialState());
  const [showClientForm, setShowClientForm] = useState(
    quotation.client?.existeSurHitech || false,
  );
  console.log("contractData", contractData);

  // Vérifier l'existence du client HITECH si identifiantHitech est présent
  useEffect(() => {
    // LS-747 : la modale se démonte à la fermeture — ne pas setState si la
    // réponse Hitech arrive après le démontage.
    let cancelled = false;

    const verifyHitechClient = async () => {
      if (!quotation.client?.identifiantHitech) {
        return;
      }

      const catalog = tools.getHitechCatalogFromGeographicZone(
        quotation.vehicle.catalog,
      );

      setHitechClientVerification({ loading: true, data: null, error: null });

      try {
        const response = await axios.get(
          `${api_url}clients/hitech/${catalog}/existe/${quotation.client.identifiantHitech}`,
        );
        if (cancelled) return;
        console.log("Vérification client HITECH:", response.data);

        // Vérifier si l'API retourne existe: false
        if (response.data && response.data.existe === false) {
          setHitechClientVerification({
            loading: false,
            data: null,
            error: "Le client n'existe pas sur HITECH",
          });
        } else {
          setHitechClientVerification({
            loading: false,
            data: response.data,
            error: null,
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error(
          "Erreur lors de la vérification du client HITECH:",
          error,
        );
        setHitechClientVerification({
          loading: false,
          data: null,
          error: error.response?.data || error.message,
        });
      }
    };

    verifyHitechClient();

    return () => {
      cancelled = true;
    };
  }, [
    quotation.client?.identifiantHitech,
    quotation.vehicle.catalog.geographicZone?.name,
    quotation.vehicle.catalog.geographic_zone,
  ]);

  const resetContractData = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir réinitialiser les valeurs ? (toutes les modifications seront perdues)",
      )
    ) {
      setContractData(getInitialState());
    }
  };

  const parseValue = (type, value) => {
    if (type === "number") {
      return Number(value);
    }
    return value;
  };

  const handleAttachClient = (clientIdHitech) => {
    setContractData({
      ...contractData,
      client: {
        ...contractData.client,
        IdHitech: clientIdHitech,
      },
    });
    setShowClientForm(true);
  };

  const handleCreateNewClient = () => {
    // Vider l'attribut IdHitech
    setContractData({
      ...contractData,
      client: {
        ...contractData.client,
        IdHitech: null,
      },
    });
    setShowClientForm(true);
  };

  const handleCancelAttachment = () => {
    // Retirer l'ID client HITECH
    setContractData({
      ...contractData,
      client: {
        ...contractData.client,
        IdHitech: null,
      },
    });
    toast.info("Rattachement au client HITECH annulé");
  };

  const handleAttachContract = (cleDossier) => {
    setContractData({
      ...contractData,
      contract: {
        ...contractData.contract,
        IdHitech: cleDossier,
      },
    });
  };

  const handleCancelContractAttachment = () => {
    // Retirer l'ID contrat HITECH
    setContractData({
      ...contractData,
      contract: {
        ...contractData.contract,
        IdHitech: null,
      },
    });
    toast.info("Rattachement au contrat HITECH annulé");
  };

  const handleChange = (e, entity) => {
    const { name, value, type, checked } = e.target;
    const newState = {
      ...contractData,
      [entity]: {
        ...contractData[entity],
        [name]: type === "checkbox" ? checked : parseValue(type, value),
      },
    };
    if (entity === entitiesEnum.CONTRACT) {
      if (name === "DateDebut") {
        newState.contract.KmFacturesDateReference = value;
        //doit prendre le numéro du mois de la date dans value (de 0 à 11)
        newState.contract.KmFacturesMoisReference =
          new Date(value).getMonth() + 1;
        newState.client.MandatDate = value;
        newState.vehicle.DateAchat = value;
        newState.vehicle.DateEntree = value;
        newState.vehicle.DateDernierKm = value;
      }
    }
    if (entity === entitiesEnum.VEHICLE) {
    }

    setContractData(newState);
  };

  const handleChangeVehicleUnique = (e, k) => {
    const { name, value, type, checked } = e.target;
    const newVehicleUniques = [...contractData.vehicleUniques];
    newVehicleUniques[k] = {
      ...newVehicleUniques[k],
      [name]: type === "checkbox" ? checked : value,
    };
    if (name === "Immat1") {
      newVehicleUniques[k].Numero = value;
    }
    const newState = {
      ...contractData,
      vehicleUniques: newVehicleUniques,
    };
    setContractData(newState);
  };

  const addVehicleUnique = () => {
    if (quotation.quantity - (contractData.vehicleUniques.length + 1) < 0) {
      toast.error(
        `Le nombre de véhicules ne peut excéder la quantité sur la ligne de devis (${quotation.quantity})`,
      );
      return;
    }
    setContractData({
      ...contractData,
      vehicleUniques: [...contractData.vehicleUniques, {}],
    });
  };

  const removeVehicleUnique = (k) => {
    setContractData({
      ...contractData,
      vehicleUniques: contractData.vehicleUniques.filter((_, i) => i !== k),
    });
  };

  const convertDataTypesRecursively = (data, schema) => {
    if (!schema || !data) return data;

    const [schemaName] = Object.keys(schema);
    const properties = schema[schemaName].properties;
    const result = {};

    Object.entries(data).forEach(([key, value]) => {
      const property = properties[key];
      if (!property) {
        result[key] = value;
        return;
      }

      switch (property.type) {
        case "integer":
          result[key] =
            value === null || value === "" ? null : parseInt(value, 10);
          break;
        case "number":
          result[key] =
            value === null || value === "" ? null : parseFloat(value);
          break;
        default:
          result[key] = value;
      }
    });

    return result;
  };

  const saveContract = () => {
    // Vérification des champs requis
    let missingFields = contractValidation(contractData);

    // Si des champs sont manquants, mettre à jour les erreurs
    if (missingFields.length > 0) {
      setValidationErrors(missingFields);
      return;
    }
    setValidationErrors([]);

    // Convertir les types selon les schémas
    const convertedData = {
      Client: convertDataTypesRecursively(
        contractData.client,
        clientSchemaWithConstants,
      ),
      Contract: convertDataTypesRecursively(
        contractData.contract,
        contractSchemaWithConstants,
      ),
      Vehicle: convertDataTypesRecursively(
        contractData.vehicle,
        vehicleSchemaWithConstants,
      ),
      VehicleUniques: convertDataTypesRecursively(
        contractData.vehicleUniques,
        vehicleUniqueSchema,
      ),
    };

    onGEnerateContract(convertedData);
  };

  return (
    <div className="contract-generator">
      {/* Affichage des erreurs de validation */}
      <div className="d-flex justify-content-end">
        <button
          onClick={resetContractData}
          className="btn btn-outline-info border-0 btn-sm"
        >
          <i className="fa fa-undo mr-2" />
          Rétablir les valeurs par défaut
        </button>
      </div>

      <ul className="nav nav-tabs" role="tablist">
        <li className="nav-item">
          <a
            className={`nav-link ${activeTab === "contrat" ? "active" : ""}`}
            onClick={() => setActiveTab("contrat")}
            role="tab"
            href="#contrat"
            data-toggle="tab"
          >
            <i className="fa fa-file-contract mr-2" />
            Contrat
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link ${activeTab === "client" ? "active" : ""}`}
            onClick={() => setActiveTab("client")}
            role="tab"
            href="#client"
            data-toggle="tab"
          >
            <i className="fa fa-user mr-2" />
            Client
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link ${activeTab === "vehicule" ? "active" : ""}`}
            onClick={() => setActiveTab("vehicule")}
            role="tab"
            href="#vehicule"
            data-toggle="tab"
          >
            <i className="fa fa-car mr-2" />
            Véhicules ({contractData.vehicleUniques.length})
          </a>
        </li>
      </ul>

      <div className="tab-content mt-3">
        <div
          className={`tab-pane fade ${
            activeTab === "contrat" ? "show active" : ""
          }`}
          id="contrat"
          role="tabpanel"
        >
          {/* Module de recherche de contrat sur HITECH */}
          <ContractSearchHitech
            contractData={contractData}
            catalog={tools.getHitechCatalogFromGeographicZone(
              quotation.vehicle.catalog,
            )}
            onAttachContract={handleAttachContract}
          />

          {/* Alerte de rattachement à un contrat existant */}
          {contractData.contract?.IdHitech && (
            <div className="alert alert-success mb-3">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="alert-heading mb-2">
                    <i className="fa fa-link mr-2" />
                    MATCH rattaché au contrat HITECH existant
                  </h6>
                  <p className="mb-2">
                    <strong>Clé dossier HITECH :</strong>{" "}
                    {contractData.contract.IdHitech}
                  </p>
                  <p className="mb-2">
                    Le contrat ne sera <strong>pas créé</strong> sur HITECH car
                    il existe déjà. MATCH enregistrera simplement les
                    informations du formulaire ci-dessous dans sa base de
                    données et établira le lien avec le contrat HITECH existant.
                  </p>
                  <p className="mb-0 small">
                    <i className="fa fa-info-circle mr-1" />
                    Si vous vous êtes trompé de contrat, vous pouvez annuler le
                    rattachement et effectuer une nouvelle recherche.
                  </p>
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-default ml-3"
                    onClick={handleCancelContractAttachment}
                    title="Annuler le rattachement"
                  >
                    <i className="fa fa-times mr-1" />
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Alerte de création d'un nouveau contrat */}
          {!contractData.contract?.IdHitech && (
            <div className="alert alert-info mb-3">
              <h6 className="alert-heading mb-2">
                <i className="fa fa-plus-circle mr-2" />
                Création d'un nouveau contrat sur HITECH
              </h6>
              <p className="mb-0">
                Aucun contrat HITECH rattaché. Un nouveau contrat sera
                automatiquement créé sur HITECH avec les informations du
                formulaire ci-dessous lors de la génération.
              </p>
            </div>
          )}

          {/* Contenu de l'onglet Contrat */}
          <FormGenerator
            schema={contractSchemaWithConstants}
            initialValues={contractData.contract}
            handleChange={(e) => handleChange(e, entitiesEnum.CONTRACT)}
            nbCols={6}
          />
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "client" ? "show active" : ""
          }`}
          id="client"
          role="tabpanel"
        >
          {/* Module de recherche de client sur HITECH */}
          <ClientSearchHitech
            contractData={contractData}
            catalog={tools.getHitechCatalogFromGeographicZone(
              quotation.vehicle.catalog,
            )}
            onAttachClient={handleAttachClient}
            clientExisteSurHitech={quotation.client?.existeSurHitech || false}
          />

          {/* Bouton pour créer un nouveau client */}
          {!quotation.client?.existeSurHitech && !showClientForm && (
            <div className="text-center mb-4">
              <button
                className="btn btn-success btn-lg"
                onClick={handleCreateNewClient}
              >
                <i className="fa fa-plus mr-2" />
                Créer un nouveau client
              </button>
              <p className="text-muted mt-2 small">
                <i className="fa fa-info-circle mr-1" />
                Si vous n'avez pas trouvé le client dans la recherche ci-dessus
              </p>
            </div>
          )}

          {/* Alerte client existant sur HITECH (depuis devis) */}
          {showClientForm &&
            quotation.client?.existeSurHitech &&
            !contractData.client?.IdHitech && (
              <div
                className={`alert mb-3 ${
                  hitechClientVerification.error
                    ? "alert-danger"
                    : "alert-success"
                }`}
              >
                <h6 className="alert-heading mb-2">
                  {hitechClientVerification.error ? (
                    <>
                      <i className="fa fa-exclamation-triangle mr-2" />
                      Client HITECH introuvable
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle mr-2" />
                      Client existant sur HITECH
                    </>
                  )}
                  {quotation.client?.identifiantHitech && (
                    <>
                      <span
                        className="badge badge-light ml-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const url = `${hitech_client_url}${quotation.client.identifiantHitech}`;
                          navigator.clipboard
                            .writeText(url)
                            .then(() => {
                              toast.success(
                                "URL copiée dans le presse-papier, collez-la dans un nouvel onglet de votre navigateur",
                              );
                            })
                            .catch(() => {
                              toast.error("Erreur lors de la copie");
                            });
                        }}
                        title="Cliquer pour copier l'URL HITECH"
                      >
                        {quotation.client.identifiantHitech}
                        <i
                          className="fa fa-copy ml-1"
                          style={{ fontSize: "0.8em" }}
                        />
                      </span>
                      {hitechClientVerification.loading && (
                        <span className="badge badge-secondary ml-2">
                          <i className="fa fa-spinner fa-spin mr-1" />
                          Vérification...
                        </span>
                      )}
                    </>
                  )}
                </h6>
                {hitechClientVerification.error ? (
                  <p className="mb-0">
                    <strong>Le client HITECH semble avoir disparu.</strong>
                    <br />
                    Cela ne devrait pas arriver à moins d'un incident technique
                    (maintenance d'Hitech). Veuillez contacter le support
                    technique.
                  </p>
                ) : (
                  <p className="mb-0">
                    Les informations ci-dessous mettront à jour le client
                    existant sur HITECH déjà rattaché lors de la création du
                    contrat.
                  </p>
                )}
              </div>
            )}

          {/* Alerte de création d'un nouveau client */}
          {showClientForm &&
            !quotation.client?.existeSurHitech &&
            !contractData.client?.IdHitech && (
              <div className="alert alert-info mb-3">
                <h6 className="alert-heading mb-2">
                  <i className="fa fa-info-circle mr-2" />
                  Aucun client rattaché
                </h6>
                <p className="mb-0">
                  En l'état, un nouveau client sera créé sur HITECH avec les
                  informations du formulaire ci-dessous lors de la création du
                  contrat.
                </p>
              </div>
            )}

          {/* Alerte de mise à jour client HITECH (recherche manuelle) */}
          {showClientForm && contractData.client?.IdHitech && (
            <div className="alert alert-warning mb-3">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="alert-heading mb-2">
                    <i className="fa fa-exclamation-triangle mr-2" />
                    Client HITECH rattaché
                  </h6>
                  <p className="mb-2">
                    Le client HITECH sera mis à jour avec les informations du
                    formulaire ci-dessous lors de la création du contrat.
                  </p>
                  <p className="mb-0 small">
                    <i className="fa fa-info-circle mr-1" />
                    Si vous vous êtes trompé de client, vous pouvez annuler le
                    rattachement.
                  </p>
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-default ml-3"
                    onClick={handleCancelAttachment}
                    title="Annuler le rattachement"
                  >
                    <i className="fa fa-times mr-1" />
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contenu de l'onglet Client */}
          {showClientForm && (
            <FormGenerator
              schema={clientSchemaWithConstants}
              initialValues={contractData.client}
              handleChange={(e) => handleChange(e, entitiesEnum.CLIENT)}
            />
          )}
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "vehicule" ? "show active" : ""
          }`}
          id="vehicule"
          role="tabpanel"
        >
          {/* Contenu de l'onglet Véhicule */}
          {contractData.vehicleUniques.map((vehicle, vk) => (
            <div className="alert bg-light-orange border">
              <div className=" d-flex justify-content-between align-items-center">
                <h2>
                  <i className="fa fa-car mr-2" />
                  Véhicule {vk + 1}
                </h2>
                <button
                  className="btn btn-sm btn-default shadow-none border-0"
                  onClick={() => removeVehicleUnique(vk)}
                >
                  <i className="fa fa-trash mr-2" />
                  Retirer ce véhicule
                </button>
              </div>
              <FormGenerator
                schema={vehicleUniqueSchema}
                initialValues={vehicle}
                handleChange={(e) => handleChangeVehicleUnique(e, vk)}
              />
            </div>
          ))}
          {contractData.vehicleUniques.length === 0 && (
            <div className="alert alert-default">
              <h5 className="alert-heading">Aucun véhicule</h5>
              <ol className=" mb-0">
                <li>Ajoutez des véhicules</li>
                <li>
                  Complétez les informations spécifiques à chaque véhicule
                </li>
                <li>
                  Complétez les informations communes à tous les véhicules
                </li>
              </ol>
            </div>
          )}
          <button
            className="btn btn-primary w-100"
            type="button"
            onClick={() => addVehicleUnique()}
          >
            <i className="fa fa-plus mr-2" />
            Ajouter un véhicule
          </button>
          <hr />
          <h2>Informations communes à tous les véhicules</h2>
          <FormGenerator
            schema={vehicleSchemaWithConstants}
            initialValues={contractData.vehicle}
            handleChange={(e) => handleChange(e, entitiesEnum.VEHICLE)}
          />
          <div className="alert alert-default px-0 py-0">
            <h5 className="alert-heading">
              <i className="fa fa-info-circle mr-2" />
              Quel type d'assurance choisir sur Hitech ?
            </h5>
            <ul>
              <li>{"Si assurance = 0 ALORS Toujours Client"}</li>
              <li>
                {
                  "Si prix d’achat HT net véhicule > 50k ALORS assurance SAAR confort obligatoire"
                }
              </li>
              <li>
                {
                  "Si prix d’achat HT net véhicule < 50k ET le loyer financier > 410€ ALORS assurance SAAR"
                }
              </li>
              <li>{"Sinon assurance HOWDEN"}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="w-100 pt-3">
        {validationErrors.length > 0 && (
          <div className="alert alert-danger mt-3" role="alert">
            <h5 className="alert-heading">
              Veuillez remplir les champs obligatoires suivants :
            </h5>
            <ul className="mb-0">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        {contractData.vehicleUniques.length === 0 && (
          <div className="alert alert-primary mt-3">
            <h4 className="text-secondary">Remarques</h4>
            <ul>
              <li>
                Vérifiez et complétez les informations du contrat, client et
                véhicules.
              </li>
              <li>Ajoutez au moins un véhicule dans l'onglet Véhicules.</li>
            </ul>
          </div>
        )}
        {contractData.vehicleUniques.length > 0 && (
          <div className="alert alert-primary mt-3">
            <h4 className="text-secondary">Remarques</h4>
            <ul>
              <li>
                Vérifiez et complétez les informations du contrat, client et
                véhicules.
              </li>
              <li>Autant de contrats que de véhicules sont créés.</li>
              <li>
                Les contrats seront créés sur HITECH. Vous pouvez ensuite les
                modifier dans le module ContratsLD.
              </li>
            </ul>
            <button
              type="button"
              className="btn btn-success w-100"
              onClick={() => saveContract()}
              disabled={state.quotation.isLoading}
            >
              {state.quotation.isLoading
                ? "Sauvegarde en cours..."
                : `Créer ${contractData.vehicleUniques.length} contrat(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractGenerator;
